/**
 * Unified trades API — GMX orders + Hyperliquid fills from usdx-backend DB.
 */

const BACKEND_URL =
  import.meta.env.VITE_BACKEND_URL || "https://backend-api.backend.usdx.live";

export type TradePlatform = "gmx" | "hyperliquid";

export type UnifiedTrade = {
  id: string;
  platform: TradePlatform;
  time: number;
  symbol: string;
  side: string;
  size: string;
  price: string;
  fee?: string;
  pnl?: string;
  orderId?: string;
  txHash?: string;
  orderType?: string;
  isLong?: boolean;
  market?: string;
};

export type FetchTradesParams = {
  address?: string;
  account?: string;
  page?: number;
  limit?: number;
  platform?: "all" | TradePlatform;
  startDate?: string;
  endDate?: string;
};

export type FetchTradesResponse = {
  status: boolean;
  data?: {
    trades: UnifiedTrade[];
    pagination: {
      total: number;
      page: number;
      pages: number;
    };
  };
  message?: string;
};

export async function fetchTrades(params: FetchTradesParams): Promise<FetchTradesResponse> {
  const {
    address,
    account,
    page = 1,
    limit = 50,
    platform = "all",
    startDate,
    endDate,
  } = params;

  const searchParams = new URLSearchParams();
  if (address) searchParams.set("address", address);
  if (account) searchParams.set("account", account);
  searchParams.set("page", String(page));
  searchParams.set("limit", String(limit));
  searchParams.set("platform", platform);
  if (startDate) searchParams.set("startDate", startDate);
  if (endDate) searchParams.set("endDate", endDate);

  const url = `${BACKEND_URL.replace(/\/$/, "")}/api/admin/trades?${searchParams.toString()}`;
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  const data = (await res.json()) as FetchTradesResponse;
  if (!res.ok) {
    throw new Error(data?.message || `Trades request failed: ${res.status}`);
  }
  return data;
}
